"use client";

import styles from "./lifestyle-section.module.css";
import { MdOutlineChevronLeft, MdOutlineChevronRight } from "react-icons/md";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useRef, useState } from "react";

const lifestyleData = [
  {
    id: "city",
    imageUrl: "/placeholder-images/city-living.png",
    title: "City Living",
    description: "Vibrant. Connected.\nEverything at your doorstep.",
  },
  {
    id: "coastal",
    imageUrl: "/placeholder-images/coastal-living.png",
    title: "Coastal Escapes",
    description: "Breathe in. Slow down.\nLive by the water.",
  },
  {
    id: "country",
    imageUrl: "/placeholder-images/countryside-living.png",
    title: "Countryside Retreats",
    description: "Nature. Peace. Privacy.\nYour sanctuary awaits.",
  },
  {
    id: "invest",
    imageUrl: "/placeholder-images/investment.png",
    title: "Investment\nOpportunities",
    description: "Smart moves. Strong returns.\nBuild your future.",
  },
  // Add 2 more items to test the carousel logic properly
  {
    id: "mountains",
    imageUrl: "/placeholder-images/coastal-living.png",
    title: "Mountain Peaks",
    description: "Crisp air. High altitudes.\nElevated living.",
  },
  {
    id: "historic",
    imageUrl: "/placeholder-images/city-living.png",
    title: "Historic Charm",
    description: "Cobblestones. Culture.\nTimeless elegance.",
  },
];

const insightsData = [
  {
    id: 1,
    imageUrl: "/placeholder-images/city-living.png",
    title: "Dubai Real Estate Market\nOutlook 2024",
    date: "May 15, 2024",
  },
  {
    id: 2,
    imageUrl: "/placeholder-images/coastal-living.png",
    title: "Top 10 Cities for Property\nInvestment",
    date: "May 10, 2024",
  },
  {
    id: 3,
    imageUrl: "/placeholder-images/investment.png",
    title: "Why International Buyers\nChoose Spain",
    date: "May 5, 2024",
  },
];

function LifestyleCard({
  imageUrl,
  title,
  description,
}: {
  imageUrl: string;
  title: string;
  description: string;
}) {
  return (
    <div
      className={styles.cardContainer}
      style={{ backgroundImage: `url(${imageUrl})` }}
    >
      <div className={styles.cardOverlay}>
        <h3 className={styles.cardTitle}>{title}</h3>
        <p className={styles.cardDescription}>{description}</p>
        <button className={styles.exploreButton} tabIndex={-1}>
          Explore
        </button>
      </div>
    </div>
  );
}

function MarketInsights() {
  return (
    <aside className={styles.insightsContainer}>
      <header className={styles.insightsHeader}>
        <h3 className={styles.insightsTitle}>Global Market Insights</h3>
        <Link href="#" className={styles.seeAllLink}>
          See all insights
        </Link>
      </header>

      <div className={styles.insightsList}>
        {insightsData.map((data) => (
          <Link href="#" key={data.id} className={styles.insightItem}>
            <Image
              src={data.imageUrl}
              alt="Insight thumbnail"
              width={80}
              height={56}
              className={styles.insightImage}
            />
            <div className={styles.insightText}>
              <h4 className={styles.insightItemTitle}>{data.title}</h4>
              <p className={styles.insightItemDate}>{data.date}</p>
            </div>
            <MdOutlineChevronRight className={styles.insightArrow} />
          </Link>
        ))}
      </div>
    </aside>
  );
}

export default function LifestyleSection() {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Drag to scroll logic
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleScrollLeft = () => {
    if (scrollRef.current) {
      const { clientWidth } = scrollRef.current;
      scrollRef.current.scrollBy({ left: -clientWidth, behavior: "smooth" });
    }
  };

  const handleScrollRight = () => {
    if (scrollRef.current) {
      const { clientWidth } = scrollRef.current;
      scrollRef.current.scrollBy({ left: clientWidth, behavior: "smooth" });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => setIsDragging(false);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  return (
    <section className={styles.wrapper}>
      <header className={styles.mainHeader}>
        <h2 className={styles.mainTitle}>Homes for every lifestyle</h2>
      </header>

      <div className={styles.contentFlex}>
        {/* Left Side: Lifestyle Cards Carousel */}
        <div className={styles.carouselWrapper}>
          <div
            className={`${styles.cardsTrack} ${isDragging ? styles.dragging : ""}`}
            ref={scrollRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            {lifestyleData.map((data) => (
              <LifestyleCard
                key={data.id}
                imageUrl={data.imageUrl}
                title={data.title}
                description={data.description}
              />
            ))}
          </div>

          <div className={styles.carouselControls}>
            <button
              className={styles.controlBtn}
              aria-label="Previous"
              onClick={handleScrollLeft}
            >
              <MdOutlineChevronLeft />
            </button>
            <button
              className={styles.controlBtn}
              aria-label="Next"
              onClick={handleScrollRight}
            >
              <MdOutlineChevronRight />
            </button>
          </div>
        </div>

        {/* Right Side: Market Insights */}
        <MarketInsights />
      </div>
    </section>
  );
}
